import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import API from './endpoints';

/**
 * Pre-configured Axios instance with interceptors for:
 *  - AuthInterceptor: Bearer token from authStore (memory only)
 *  - TenantIdInterceptor: X-Tenant-Id header from authStore
 *  - TransactionIdInterceptor: UUID v4 X-Transaction-Id per request
 *  - ErrorInterceptor: 401 → redirect to login, 403/500 handling
 */
const axiosClient = axios.create({
  // Prod builds resolve same-origin (relative '') so a missing env var never
  // silently points at a dev's localhost; behind the Caddy reverse proxy /api
  // is on the same origin. Dev keeps the explicit localhost gateway. (T6.1)
  baseURL: import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? 'http://localhost:5001' : ''),
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Generate a UUID v4 for transaction tracing. */
function generateTransactionId(): string {
  return crypto.randomUUID();
}

// ─── Request Interceptors ────────────────────────────────────────

axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = useAuthStore.getState();

    // AuthInterceptor — attach JWT from memory
    if (state.token) {
      config.headers.Authorization = `Bearer ${state.token}`;
    }

    // TenantIdInterceptor — attach tenant context
    // On login, user is null but tenantId is in the request body
    if (state.user?.tenantId) {
      config.headers['X-Tenant-Id'] = state.user.tenantId;
    } else if (config.data?.tenantId) {
      config.headers['X-Tenant-Id'] = config.data.tenantId;
    }

    // TransactionIdInterceptor — unique trace ID per request
    config.headers['X-Transaction-Id'] = generateTransactionId();

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptors ───────────────────────────────────────
//
// OP-335 — a 401 used to hard-log-out immediately with zero refresh-and-retry,
// unlike admin-portal's axiosClient (which grew a sharedRefresh + `_retry`
// interceptor after a 2026-05-11 cascading-refresh incident). A flaky moment
// or a wake-from-sleep race could 401 a request whose refresh token was still
// perfectly valid and boot the agent mid-shift. Ports the same pattern here:
// concurrent 401s share one refresh; only a GENUINE refresh failure logs out.

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('no-refresh-token');
  // Bare axios (NOT axiosClient) so this call doesn't recurse through the
  // response interceptor it's running inside of.
  const { data } = await axios.post<{ accessToken: string }>(
    `${axiosClient.defaults.baseURL}${API.AUTH.REFRESH}`,
    { refreshToken },
  );
  useAuthStore.getState().updateToken(data.accessToken);
  return data.accessToken;
}

function sharedRefresh(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refreshAccessToken().finally(() => {
    // Allow the next refresh cycle to start fresh after this one completes
    // (success or failure).
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// Calls made BEFORE a session exists. A 401 from one of these is the ANSWER to the call
// (wrong password, locked account, bad reset token) and not an expired session: there is
// nothing to refresh, and redirecting to /login would hard-reload the page and wipe the very
// error message the user is meant to read, together with the typed fields and the ?tenant=
// prefill. (Found 2026-10-09: every wrong password blinked the error and reloaded the page.)
const ANONYMOUS_AUTH_PATHS: string[] = [
  API.AUTH.LOGIN,
  API.AUTH.REFRESH,
  API.AUTH.CHANGE_PASSWORD,
  API.AUTH.HANDOFF_REDEEM,
  API.AUTH.FORGOT_PASSWORD,
  API.AUTH.RESET_PASSWORD,
  API.AUTH.VALIDATE_ACTION_TOKEN,
];

function isAnonymousAuthCall(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? '';
  return ANONYMOUS_AUTH_PATHS.some((p) => url === p || url.startsWith(`${p}?`));
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status } = error.response;
    const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (status === 401 && config && !config._retry && !isAnonymousAuthCall(config)) {
      config._retry = true;
      try {
        const newToken = await sharedRefresh();
        config.headers.Authorization = `Bearer ${newToken}`;
        return axiosClient(config);
      } catch {
        // Refresh genuinely failed (no refresh token, refresh token expired
        // / revoked, server unreachable). Clear local state and redirect —
        // this is the only path that should still log the agent out.
        useAuthStore.getState().logout();
        // Already on the login page (e.g. a stale 401 from a background call): a reload would
        // only throw away what the user is looking at. Same guard as admin-portal.
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// ─── Type augmentation for the retry flag ────────────────────────

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

export default axiosClient;
